import type { Permission } from '@idc-forms/permissions'
import {
  BadgeCheck,
  Building,
  GraduationCap,
  House,
  HousePlug,
  HousePlus,
  KeyIcon,
  Landmark,
  type LucideIcon,
  Network,
  ScrollText,
  Settings2,
  Store,
  UserCog2,
  UsersRound
} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation } from 'react-router'
import { preloadAdminRoute } from '@/app/router/route-preloaders'
import { PATHS } from '@/app/router/routes'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem } from '@/shared/ui/sidebar'

interface BusinessItem {
  label: string
  icon: LucideIcon
  to: string
  permission: Permission
}

const BUSINESS_ITEMS: BusinessItem[] = [
  {
    label: 'Usuarios',
    icon: UsersRound,
    to: PATHS.admin.business.users,
    permission: 'ReadUsers' as Permission
  },
  {
    label: 'Roles',
    icon: KeyIcon,
    to: PATHS.admin.business.roles,
    permission: 'ReadRoles' as Permission
  },
  {
    label: 'Empresas',
    icon: Building,
    to: PATHS.admin.business.companies,
    permission: 'ReadCompanies' as Permission
  },
  {
    label: 'Agencias',
    icon: Store,
    to: PATHS.admin.business.agencies,
    permission: 'ReadAgencies' as Permission
  },
  {
    label: 'Sociedades',
    icon: Landmark,
    to: PATHS.admin.business.societies,
    permission: 'ReadSocieties' as Permission
  },
  {
    label: 'Proyectos',
    icon: HousePlus,
    to: PATHS.admin.business.projects.root,
    permission: 'ReadProjects' as Permission
  },
  {
    label: 'Subtipos',
    icon: HousePlug,
    to: PATHS.admin.business.typeProperties.root,
    permission: 'ReadTypeProperties' as Permission
  },
  {
    label: 'Propiedades',
    icon: House,
    to: PATHS.admin.business.properties,
    permission: 'ReadProperties' as Permission
  },
  {
    label: 'Representantes Legales',
    icon: ScrollText,
    to: PATHS.admin.business.representants,
    permission: 'ReadRepresentants' as Permission
  },
  {
    label: 'Estados',
    icon: BadgeCheck,
    to: PATHS.admin.business.states,
    permission: 'ReadStates' as Permission
  },
  {
    label: 'Flujos de trabajo',
    icon: Network,
    to: PATHS.admin.business.workflows,
    permission: 'ReadWorkflows' as Permission
  },
  {
    label: 'Profesiones',
    icon: GraduationCap,
    to: PATHS.admin.business.professions,
    permission: 'ReadProfessions' as Permission
  },
  // ⚠️ VA DESPUÉS DE PROFESIONES, y su permiso también va después de Profesiones en el tab
  // «Empresa» del catálogo (`packages/permissions/src/catalog.ts`), para que quien busca la
  // pantalla y quien busca su permiso lo encuentren en el mismo tramo. Si movés uno, mirá el otro.
  {
    label: 'Configuración de Créditos',
    icon: Settings2,
    to: PATHS.admin.business.creditProcess,
    permission: 'ManageCreditTemplates' as Permission
  },
  {
    label: 'Configuración de Clientes',
    icon: UserCog2,
    to: PATHS.admin.business.clientConfig,
    permission: 'ReadStates' as Permission
  }
]

export function useHasBusinessItems() {
  const { has, isAdmin } = usePermissions()
  return useMemo(() => {
    if (isAdmin) return true
    return BUSINESS_ITEMS.some((item) => has(item.permission))
  }, [isAdmin, has])
}

const MenuBusiness = () => {
  const pathname = useLocation().pathname
  const { has, isAdmin } = usePermissions()

  const visibleItems = useMemo(() => {
    if (isAdmin) return BUSINESS_ITEMS
    return BUSINESS_ITEMS.filter((item) => has(item.permission))
  }, [isAdmin, has])

  if (visibleItems.length === 0) return null

  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem>
        {visibleItems.map((item) => {
          const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`)

          return (
            <SidebarMenuSubButton
              key={item.to}
              asChild
              isActive={isActive}
              className="data-[active=true]:font-semibold data-[active=true]:text-sidebar-primary"
            >
              <Link
                to={item.to}
                aria-current={isActive ? 'page' : undefined}
                onFocus={() => {
                  void preloadAdminRoute('business')
                }}
                onPointerEnter={() => {
                  void preloadAdminRoute('business')
                }}
              >
                <item.icon />
                <span>{item.label}</span>
              </Link>
            </SidebarMenuSubButton>
          )
        })}
      </SidebarMenuSubItem>
    </SidebarMenuSub>
  )
}

export default MenuBusiness
